/**
 * useGeolocation — Hook pour gérer la géolocalisation sur mobile/web
 * 
 * Gère:
 *   - Demande de permission au premier appel
 *   - Cache localStorage (30 jours)
 *   - Retry logic avec exponential backoff
 *   - Fallback sur position en cache
 *   - États loading/error
 */
import { useEffect, useState, useRef, useCallback } from 'react';
import { geolocationService } from '../services/geolocationService';
import { toast } from 'sonner';

export const useGeolocation = (options = {}) => {
  const {
    onSuccess = null,
    onError = null,
    immediate = false, // Demander la permission au montage
    requestOnMissing = false, // Demander si pas de cache
    silent = false, // Ne pas afficher les toasts
  } = options;

  const [coords, setCoords] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [permissionState, setPermissionState] = useState(null);
  const attemptRef = useRef(0);
  const immediateRanRef = useRef(false); // Guard: only fetch once on mount

  // Charger la position en cache au montage
  useEffect(() => {
    const cached = geolocationService.getCachedLocation();
    if (cached?.lat && cached?.lng) {
      setCoords({ lat: cached.lat, lng: cached.lng });
      onSuccess?.({ lat: cached.lat, lng: cached.lng });
    }
  }, [onSuccess]);

  // Vérifier l'état de permission au montage
  useEffect(() => {
    const checkPermission = async () => {
      try {
        const state = await geolocationService.getPermissionState();
        setPermissionState(state);
      } catch {
        setPermissionState('unknown');
      }
    };
    checkPermission();
  }, []);

  // Demander la position
  const requestPosition = useCallback(async (force = false) => {
    if (loading && !force) return;

    // Si on a déjà une position et qu'on ne force pas
    if (coords && !force) return;

    setLoading(true);
    setError(null);
    attemptRef.current = 0;

    try {
      const position = await geolocationService.requestCurrent({
        timeout: 12000,
        highAccuracy: false,
        maxRetries: 2,
      });

      const newCoords = { lat: position.lat, lng: position.lng };
      setCoords(newCoords);
      setPermissionState('granted');
      onSuccess?.(newCoords);

      if (!silent) {
        toast.success('Position détectée');
      }
    } catch (err) {
      const message = err.message || 'Impossible de détecter la position';
      setError(message);
      setPermissionState('denied');
      onError?.(err);

      if (!silent) {
        // Ne pas afficher de toast si l'utilisateur a refusé
        if (err.kind !== 'denied') {
          // toast.error(message);
        }
      }

      // Fallback sur cache
      const cached = geolocationService.getCachedLocation();
      if (cached?.lat && cached?.lng) {
        setCoords({ lat: cached.lat, lng: cached.lng });
        setError(null); // Effacer l'erreur si on a un cache
      }
    } finally {
      setLoading(false);
    }
  }, [coords, loading, onSuccess, onError, silent]);

  // Demande de position au montage si immediate=true — une seule fois
  // NOTE: deps intentionnellement vides pour éviter la boucle infinie
  // (requestPosition change à chaque render car useCallback dépend de loading/coords)
  useEffect(() => {
    if (immediate && !immediateRanRef.current) {
      immediateRanRef.current = true;
      requestPosition();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Demander si pas de coords et requestOnMissing=true — une seule fois au montage
  useEffect(() => {
    if (requestOnMissing && !coords) {
      requestPosition();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    coords,
    loading,
    error,
    permissionState,
    requestPosition,
    hasPermission: permissionState === 'granted',
    isSupported: geolocationService.isSupported?.(),
  };
};

export default useGeolocation;
